import { hideAllPoppers } from "floating-vue";

/**
 * Every ui-library dropdown — field-style selects (HRDropdown) and action menus (HRDropdownActions)
 * alike — made usable from the keyboard and understandable to a screen reader, app-wide.
 *
 * The library's dropdowns move a highlight with the arrow keys and pick with Enter, but:
 * - their buttons never said they open anything, or whether it was open, and floating-vue tied the
 *   popup to its button with aria-describedby — so a screen reader read the whole list out as the
 *   button's description;
 * - the list had no role, and the highlight was never announced, focus staying on the button (or
 *   the search field) the whole time;
 * - a menu opened from the keyboard highlighted nothing, so Enter did nothing until ↓ was pressed;
 * - Escape out of a searchable list, or Tab out of any, left focus nowhere.
 *
 * floating-vue marks a dropdown's button with `data-popper-shown` while its popup is open, which is
 * what this follows. Forms using useFieldKeyboard handle Tab inside their own dropdowns first.
 */
export default defineNuxtPlugin(() => {
  type OpenDropdown = { trigger: HTMLElement; popup: HTMLElement; kind: "listbox" | "menu" };

  const open = new Map<string, OpenDropdown>();
  let ids = 0;
  let keyboardLast = false;

  const FOCUSABLE = "button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex='-1'])";

  /** The focusable control in what floating-vue calls the target: the button itself, or a wrapper. */
  const controlIn = (target: HTMLElement) =>
    target.matches(FOCUSABLE) ? target : target.querySelector<HTMLElement>(FOCUSABLE);

  const items = (popup: HTMLElement) => [...popup.querySelectorAll<HTMLElement>(".dropdown-item")];

  /** Gives the list and its items their roles and ids, as often as the list redraws (a search narrowing it). */
  const labelList = ({ popup, kind }: OpenDropdown) => {
    const list = items(popup);
    const container = list[0]?.parentElement;
    if (container && container.getAttribute("role") !== kind) container.setAttribute("role", kind);
    for (const item of list) {
      if (!item.id) item.id = `dropdown-option-${++ids}`;
      if (kind === "menu" && item.getAttribute("role") !== "menuitem") item.setAttribute("role", "menuitem");
    }
  };

  /** Points whatever has focus — the button, or the list's search field — at the highlighted item. */
  const announceHighlight = (dropdown: OpenDropdown) => {
    const highlighted = dropdown.popup.querySelector<HTMLElement>(".dropdown-item.focused");
    const active = document.activeElement as HTMLElement | null;
    const owner =
      active && (active === dropdown.trigger || dropdown.popup.contains(active)) ? active : dropdown.trigger;
    if (highlighted?.id) owner.setAttribute("aria-activedescendant", highlighted.id);
    else owner.removeAttribute("aria-activedescendant");
  };

  const onOpen = (target: HTMLElement) => {
    const popupId = target.getAttribute("aria-describedby");
    const popup = popupId ? document.getElementById(popupId) : null;
    const trigger = controlIn(target);
    // tooltips and hover cards keep floating-vue's description; only lists are dropdowns here
    if (!popupId || !popup || !trigger || !popup.querySelector(".dropdown-item")) return;

    const kind = trigger.id.startsWith("dropdown-select") ? "listbox" : "menu";
    const dropdown: OpenDropdown = { trigger, popup, kind };
    open.set(popupId, dropdown);

    target.removeAttribute("aria-describedby");
    trigger.setAttribute("aria-haspopup", kind);
    trigger.setAttribute("aria-expanded", "true");
    trigger.setAttribute("aria-controls", popupId);
    labelList(dropdown);

    // a menu opened from the keyboard starts on its first item, for Enter to take at once
    if (kind === "menu" && keyboardLast) {
      requestAnimationFrame(() => {
        if (!popup.querySelector(".dropdown-item.focused")) {
          document.dispatchEvent(new KeyboardEvent("keydown", { key: "ArrowDown", bubbles: true }));
        }
      });
    }
    announceHighlight(dropdown);
  };

  const onClose = (target: HTMLElement) => {
    for (const [popupId, dropdown] of open) {
      if (!target.contains(dropdown.trigger)) continue;
      open.delete(popupId);
      dropdown.trigger.setAttribute("aria-expanded", "false");
      dropdown.trigger.removeAttribute("aria-activedescendant");
      // focus that was in the list (its search field) went with it: back to the button
      requestAnimationFrame(() => {
        if (!document.activeElement || document.activeElement === document.body) dropdown.trigger.focus();
      });
    }
  };

  const dropdownHolding = (element: Element | null) => {
    for (const dropdown of open.values()) {
      if (element && (dropdown.popup.contains(element) || dropdown.trigger === element)) return dropdown;
    }
    return undefined;
  };

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const node = mutation.target as HTMLElement;
      if (mutation.type === "attributes" && mutation.attributeName === "data-popper-shown") {
        if (node.hasAttribute("data-popper-shown")) onOpen(node);
        else onClose(node);
      } else if (mutation.type === "attributes" && node.classList?.contains("dropdown-item")) {
        const dropdown = dropdownHolding(node);
        if (dropdown) announceHighlight(dropdown);
      } else if (mutation.type === "childList") {
        const dropdown = dropdownHolding(node);
        if (dropdown) labelList(dropdown);
      }
    }
  }).observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["data-popper-shown", "class"],
  });

  // the library tells keyboard from pointer the same way, for a menu's first highlight
  document.addEventListener(
    "keydown",
    (event) => {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) keyboardLast = true;
    },
    true
  );
  document.addEventListener("pointerdown", () => (keyboardLast = false), true);

  /**
   * Escape and Tab, last of all — a form's own keyboard handling (useFieldKeyboard) has had its say
   * and marks what it took. Escape: the library closes the list; focus goes back to its button. Tab:
   * the list closes and focus returns to its button, from where Tab carries on.
   */
  window.addEventListener("keydown", (event) => {
    if (event.defaultPrevented || (event.key !== "Escape" && event.key !== "Tab")) return;
    const dropdown = dropdownHolding(document.activeElement);
    if (!dropdown) return;

    if (event.key === "Escape") {
      requestAnimationFrame(() => dropdown.trigger.focus());
      return;
    }
    // Tab from the button of an open menu just closes it and moves on; from inside a list, it
    // returns to the button first
    if (dropdown.popup.contains(document.activeElement)) {
      event.preventDefault();
      dropdown.trigger.focus();
    }
    hideAllPoppers();
  });
});
